import { Archive, EllipsisVertical, Plus } from "lucide-react";
import type { ReactElement } from "react";

import { cn } from "~/lib/utils";
import type { SessionFace } from "~/neucleos/sessionFace";
import { moreUrgentRootSessionStatus, type RootSessionStatus } from "~/rootSessionStrip";
import { RoomStatusIndicator, roomStatusDescription } from "./RoomStatusIndicator";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";
import { RailHoverCard } from "./RailHoverCard";
import type { RailHoverFacts } from "./railHoverCard.logic";
import { PipCluster } from "./PipCluster";

/** Named Rooms reclaim the old glyph space. A fixed gutter shows status,
 * and a tooltip names the full Room breakdown on pointer hover and focus.
 * The header folds the Room; menu and archive remain independent actions. */
export function RoomHeader({
  roomId,
  label,
  standing,
  sessionStatuses,
  open,
  current,
  wide,
  onToggle,
  onMenu,
  onClose,
  onNewSession,
  archiveBusy = false,
  renaming = null,
  preview = null,
  previewDisabled = false,
}: {
  readonly roomId: string;
  readonly label: string;
  /** Inline rename (2026-09-10): the room's own label, committed through the World door. */
  readonly renaming?: {
    readonly title: string;
    readonly onChange: (title: string) => void;
    readonly onCommit: () => void;
    readonly onCancel: () => void;
  } | null;
  readonly standing: boolean;
  readonly face: SessionFace;
  readonly sessionStatuses: ReadonlyArray<RootSessionStatus | null>;
  readonly open: boolean;
  /** The addressed session lives in this room. */
  readonly current: boolean;
  readonly wide: boolean;
  readonly onToggle: () => void;
  /** Absent where a group has no menu of its own (RM-CAN-14: a Local folder); the seat is not drawn. */
  readonly onMenu?: ((position: { x: number; y: number }) => void) | undefined;
  /** Absent where a group cannot be archived (RM-CAN-14: a Local folder); the seat is not drawn. */
  readonly onClose?: (() => void) | undefined;
  /** RM-CAN-14: the group's own new-session seat, always in view — an empty folder's one way in. */
  readonly onNewSession?: (() => void) | undefined;
  readonly archiveBusy?: boolean;
  /** RM-CAN-10 hover card (2026-09-15): the room's full name, counts and times beside the rail. */
  readonly preview?: RailHoverFacts | null;
  readonly previewDisabled?: boolean;
}): ReactElement {
  const title = roomStatusDescription(label, sessionStatuses);
  const status = sessionStatuses.reduce<RootSessionStatus | null>(
    moreUrgentRootSessionStatus,
    null,
  );
  const header = (
    <div
      data-room-rail-fold={roomId}
      aria-expanded={open}
      aria-label={title}
      className={cn(
        "group/room relative flex min-w-0 cursor-pointer items-center rounded-lg outline-hidden transition-colors hover:bg-accent/30 focus-visible:ring-1 focus-visible:ring-ring",
        wide ? "h-[22px] w-full gap-1.5 px-1" : "size-7 shrink-0 justify-center",
      )}
      data-room-rail-room={roomId}
      data-room-rail-room-current={current ? "true" : undefined}
      data-room-rail-room-standing={standing ? "true" : undefined}
      onClick={onToggle}
      onContextMenu={(event) => {
        if (!onMenu) return;
        event.preventDefault();
        onMenu({ x: event.clientX, y: event.clientY });
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onToggle();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <span className="ncl-room-status-gutter" data-room-status-gutter="">
        <RoomStatusIndicator status={status} />
      </span>
      {!wide ? (
        <span
          aria-hidden="true"
          className="text-xs font-semibold text-muted-foreground"
          data-room-identity=""
        >
          {Array.from(label.trim())[0]?.toUpperCase() ?? "R"}
        </span>
      ) : null}
      {wide ? (
        <span
          className={cn(
            // Sentence case at the rail's reading size (2026-09-15): a room name is
            // content, not a section label, and tracked capitals truncate first.
            "min-w-0 flex-1 truncate text-xs font-semibold",
            current || standing ? "text-neucleos-identity" : "text-foreground",
          )}
        >
          {renaming ? (
            <input
              autoFocus
              className="min-w-0 flex-1 bg-transparent text-xs text-foreground outline-none"
              data-room-rail-room-rename={roomId}
              onBlur={() => renaming.onCommit()}
              onChange={(event) => renaming.onChange(event.target.value)}
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => {
                event.stopPropagation();
                if (event.key === "Enter") renaming.onCommit();
                else if (event.key === "Escape") renaming.onCancel();
              }}
              value={renaming.title}
            />
          ) : (
            label
          )}
        </span>
      ) : null}
      {wide && !open && sessionStatuses.length > 0 ? (
        <PipCluster noun="sessions" statuses={sessionStatuses} />
      ) : null}
      {/* FD-CAN-20 law 5, one level up: ⋮ before archive, revealed on hover AND
          focus-within, space never reflows the label. */}
      {wide && onMenu ? (
        <span
          role="button"
          tabIndex={0}
          aria-label={`${label} room menu`}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              event.stopPropagation();
              const rect = event.currentTarget.getBoundingClientRect();
              onMenu({ x: rect.left, y: rect.bottom + 4 });
            }
          }}
          className="hidden size-3.5 shrink-0 items-center justify-center rounded-sm opacity-60 hover:bg-accent hover:opacity-100 group-hover/room:inline-flex group-focus-within/room:inline-flex"
          data-room-rail-menu={roomId}
          onClick={(event) => {
            event.stopPropagation();
            const rect = event.currentTarget.getBoundingClientRect();
            onMenu({ x: rect.left, y: rect.bottom + 4 });
          }}
        >
          <EllipsisVertical aria-hidden className="size-3" />
        </span>
      ) : null}
      {wide && onClose ? (
        <span
          role="button"
          tabIndex={0}
          aria-label={`Archive ${label} room`}
          title={archiveBusy ? "Archiving room…" : "Archive room"}
          aria-disabled={archiveBusy}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              event.stopPropagation();
              if (!archiveBusy) onClose();
            }
          }}
          className="hidden size-3.5 shrink-0 items-center justify-center rounded-sm opacity-60 hover:bg-accent hover:opacity-100 group-hover/room:inline-flex group-focus-within/room:inline-flex"
          data-room-rail-close={roomId}
          data-room-rail-archive={roomId}
          onClick={(event) => {
            event.stopPropagation();
            if (!archiveBusy) onClose();
          }}
        >
          <Archive aria-hidden className="size-3" />
        </span>
      ) : null}
      {/* RM-CAN-14: seated LAST so the hover-revealed glyphs open to its left and the
          seat never moves under the pointer (witnessed 20 September: it slid 26px). */}
      {wide && onNewSession ? (
        <span
          role="button"
          tabIndex={0}
          aria-label={`New session in ${label}`}
          title="New session"
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              event.stopPropagation();
              onNewSession();
            }
          }}
          className="inline-flex size-3.5 shrink-0 items-center justify-center rounded-sm opacity-60 hover:bg-accent hover:opacity-100"
          data-room-rail-new-session={roomId}
          onClick={(event) => {
            event.stopPropagation();
            onNewSession();
          }}
        >
          <Plus aria-hidden className="size-3" />
        </span>
      ) : null}
    </div>
  );
  if (preview) return <RailHoverCard disabled={previewDisabled} facts={preview} trigger={header} />;
  return (
    <Tooltip>
      <TooltipTrigger render={header} />
      <TooltipPopup role="tooltip" side="right">
        {title}
      </TooltipPopup>
    </Tooltip>
  );
}
